import { DegreePlan_LearnerProgressState as LearnerProgressState } from '__generated__/graphql-types';

import type { UseCurriculum_PersonalizedSegmentCourseFragment as DpPersonalizedSegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type {
  UseCurriculum_DegreeCategoryFragment as DpCategory,
  UseCurriculum_DegreeCurriculumFragment as DpDegreeCurriculum,
  UseCurriculum_PbaCurriculumFragment as DpPbaCurriculum,
  UseCurriculum_SegmentFragment as DpSegment,
  UseCurriculum_GradeScaleFragment as GradeScaleFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import type {
  CreditsAndCoursesSummary,
  CreditsSummary,
  GPA,
  ProgressionCalculatorResult,
  Warnings,
} from 'bundles/degree-home/components/v3-dpm/types/gradeTypes';
import {
  GPA_CALCULATION_FOUR_DECIMAL_SCALE_FACTOR,
  GPA_CALCULATION_TWO_DECIMAL_SCALE_FACTOR,
  getSumOfCredits,
  roundGpa,
} from 'bundles/program-loggedin-shared/libraries/gradeUtils';

/**
 * Convert to an integer that can represent up to four decimal digits:
 * e.g., 1.2345 -> 12345, 10.34 -> 103400
 * Note: if we receive a number that exceeds four decimal digits,
 * we should round it to 4 decimal digits, e.g., 1.23456 -> 12346
 * @param inputFloat the input number to be converted
 * @returns the converted integer
 */
export const convertAndRoundFloatToFourDecimal = (inputFloat: number): number => {
  return Math.round(inputFloat * GPA_CALCULATION_FOUR_DECIMAL_SCALE_FACTOR);
};

/**
 * Convert and round the start and end values of the buckets in the grade scale
 * so that they operate on whole integers, to avoid JS' behaviour with floats
 * @param gradeScale the list of buckets and the corresponding credit point
 * @returns the converted grade scale
 */
export const convertGradeScales = (gradeScale: GradeScaleFragment) => {
  return {
    ...gradeScale,
    buckets: gradeScale.buckets.map((bucket) => ({
      ...bucket,
      gradeRangeStart: convertAndRoundFloatToFourDecimal(bucket.gradeRangeStart),
      gradeRangeEnd: convertAndRoundFloatToFourDecimal(bucket.gradeRangeEnd),
      gradePoint: convertAndRoundFloatToFourDecimal(bucket.gradePoint),
    })),
  };
};

/**
 * Map the learner's grade to a credit point, based on the institution's credit point scale
 *  The default grade scale value for an institution maps to:
 * .93-1.0 -> 4.0 credit points
 * .90-.92 -> 3.7 credit points
 * .87-.89 -> 3.3 credit points
 * .83-.86 -> 3 credit points
 * .80-.82 -> 2.7 credit points
 * .77-.79 -> 2.3 credit points
 * .73-.76 -> 2 credit points
 * .70-.72 -> 1.7 credit points
 * .67-.69 -> 1.3 credit points
 * .63-.66 -> 1.0 credit points
 * .60-.62 -> 0.7 credit points
 * 0-.59 -> 0 credit points
 *
 * each interval is a closed interval where the lower bound is inclusive, and the upper bound is exclusive
 * for each grade, it should be rounded to 4 decimal points and converted to an integer before assigning the interval,
 * e.g. 0.934567 -> 9346
 * the input learner grade is on a scale of 100 (instead of 1.0),
 * to make the grade comparable with the converted buckets in the grade scale,
 * it needs to be converted to be on the same scale, e.g. 93.4567 -> 9346
 *
 * @param convertedAndRoundedGrade the learner grade, on a scale of 10000, e.g., 0.934567 is passed in as 9346
 * @param convertedAndRoundedGrade the GPA Grade scale used to map grades to GPA points for the degree, scaled to 10000
 * @returns the corresponding credit point based on the mapping rule
 */
export const getCreditPoint = (
  convertedAndRoundedGrade: number,
  convertedGradeScale: GradeScaleFragment
): number | null => {
  const gradeScaleEntry = convertedGradeScale.buckets.find(
    (bucket) => convertedAndRoundedGrade >= bucket.gradeRangeStart && convertedAndRoundedGrade < bucket.gradeRangeEnd
  );

  return gradeScaleEntry ? gradeScaleEntry.gradePoint : null;
};

/**
 * Calculate the learner's GPA based on a list of credit hours and the corresponding credit points,
 * the GPA is calculated as a weighted average: sum(credit points * credit values per course) / sum(credit values per course)
 *
 * @param courseCreditPoints the list of credit points of the courses
 * @param courseCreditValues the list of credit values of the courses
 * @returns the calculated GPA, rounded to 2 decimal points
 */
export const getGPA = (courseCreditPoints: number[], courseCreditValues: number[]): GPA => {
  if (courseCreditPoints.length !== courseCreditValues.length || courseCreditPoints.length === 0) {
    return null;
  }
  const learnerCreditHoursSum = courseCreditValues.reduce((partialSum, creditHour) => partialSum + creditHour, 0);
  if (learnerCreditHoursSum === 0) {
    return null;
  }
  const weightedSum = courseCreditPoints.reduce(
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    (partialWeightedSum, creditPoint, index) => partialWeightedSum + creditPoint * courseCreditValues[index],
    0
  );
  const scaledGpa = weightedSum / learnerCreditHoursSum;
  // the grade points are scaled, convert back to the normal scale
  const actualGpa = scaledGpa / GPA_CALCULATION_FOUR_DECIMAL_SCALE_FACTOR;

  return roundGpa(actualGpa);
};

/**
 * Course is deemed as failed if grade is a numeric value and the learner progress
 * state is NotStarted. See: https://coursera.slack.com/archives/C058TNFJMT3/p1693409158787639
 * @param grade learner's course grade
 * @param state learners course progress state
 */
export const isCourseFailed = (grade: number | null | undefined, state: LearnerProgressState) =>
  typeof grade === 'number' && state === LearnerProgressState.NotStarted;

/**
 * Extract the courses with the COMPLETE state and grades from a list of courses
 * @param courses the given list of courses
 * @returns the courses with the COMPLETE state and grades
 */
const getCompletedCourses = (courses: DpPersonalizedSegmentCourse[]): DpPersonalizedSegmentCourse[] => {
  return courses.filter(
    (course) =>
      course.learnerProgress.state === LearnerProgressState.Completed &&
      typeof course.learnerProgress.grade === 'number'
  );
};

/**
 * Extract the courses with the FAILED state and grades from a list of courses
 * Note: The decision has been made to not display FAILED status to learners. Instead, we will
 * consider the learner's progress as FAILED if their state is NOT_STARTED, and they have a
 * non-null grade value. See: https://coursera.slack.com/archives/C058TNFJMT3/p1693409158787639
 * @param courses the given list of courses
 * @returns the courses with the FAILED state and grade
 */
const getFailedCourses = (courses: DpPersonalizedSegmentCourse[]): DpPersonalizedSegmentCourse[] => {
  return courses.filter((course) => isCourseFailed(course.learnerProgress.grade, course.learnerProgress.state));
};

/**
 * Calculate the learner's GPA for a list of courses, the calculation only considers completed courses with grades
 * @param courses the list of courses
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns the calculated GPA for the given courses, rounded to 2 decimal points
 */
export const getGPAFromCourses = (courses: DpPersonalizedSegmentCourse[], gradeScale: GradeScaleFragment): GPA => {
  const completedCourses = getCompletedCourses(courses);
  const failedCourses = getFailedCourses(courses);

  const coursesWithGrades = [...completedCourses, ...failedCourses];

  const courseCreditPoints: number[] = [];
  const courseCreditValues: number[] = [];
  const convertedGradeScales = convertGradeScales(gradeScale);
  coursesWithGrades.forEach((course) => {
    const grade = course.learnerProgress.grade;
    // check invalid course grade and do early return. Grades > 100 are explicitly supported.
    if (grade === undefined || grade === null || isNaN(grade) || grade < 0) {
      return;
    }
    // the learner's grade is provided as a percentage (on a scale of 100 instead of 1.0) , e.g. 94.117736816406 instead of 0.94117736816406
    // thus, to convert the grade to be at the same precision with the converted grade scales
    // the grade should multiply 100 instead of 10000: 92.94117736816406-> 9294
    const convertedAndRoundedGrade = Math.round(grade * GPA_CALCULATION_TWO_DECIMAL_SCALE_FACTOR);
    const courseCreditPoint = getCreditPoint(convertedAndRoundedGrade, convertedGradeScales);
    if (courseCreditPoint === null) {
      return;
    }
    courseCreditPoints.push(courseCreditPoint);
    courseCreditValues.push(course.segmentCourse.creditValue);
  });

  return getGPA(courseCreditPoints, courseCreditValues);
};

/**
 * Calculate the learner's GPA for all the completed courses in the given category
 * @param category the category
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns the calculated GPA for all the completed courses in the given category, rounded to 2 decimal points
 */
export const getGPAForSingleCategory = (category: DpCategory, gradeScale: GradeScaleFragment): GPA => {
  const courses = category.segments.map((segment) => segment.courses).flat();
  return getGPAFromCourses(courses, gradeScale);
};

/**
 * Calculate the learner's GPA for all the completed courses in a list of categories
 * @param categories the list of categories
 * @returns the calculated GPA for all the completed courses in the given categories, rounded to 2 decimal points
 */
const getGPAForCategories = (categories: DpCategory[], gradeScale: GradeScaleFragment): GPA => {
  const segments = categories.map((category) => category.segments).flat();
  const courses = segments.map((segment) => segment.courses).flat();
  return getGPAFromCourses(courses, gradeScale);
};

/**
 * Calculate the learner's GPA for all the completed courses in a curriculum
 * @param curriculum the curriculum
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns the calculated GPA for all the completed courses in the given curriculum, rounded to 2 decimal points
 */
export const getGPAForSingleCurriculum = (
  curriculum: DpDegreeCurriculum | DpPbaCurriculum,
  gradeScale: GradeScaleFragment
): GPA => {
  return getGPAForCategories(curriculum.categories, gradeScale);
};

/**
 * Calculate the learner's GPA for all the completed courses in a list of curriculums
 * @param curriculums the list of curriculums
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns the calculated GPA for all the completed courses in the given curriculums, rounded to 2 decimal points
 */
export const getGPAForCurriculums = (
  curriculums: (DpDegreeCurriculum | DpPbaCurriculum)[],
  gradeScale: GradeScaleFragment
): GPA => {
  const categories = curriculums.map((curriculum) => curriculum.categories).flat();
  return getGPAForCategories(categories, gradeScale);
};

/**
 * Return an array of unique courses
 * @param courses an array of courses
 * @returns {Array} of courses that do not share the same coursera id
 */
export const dedupeByCourseraCourseId = (courses: DpPersonalizedSegmentCourse[]) => {
  // we are deduping using coursera-id: which means that non-coursera courses will not be deduped
  // in the first iteration of the product we get course progression of coursera courses
  const courseraIds = courses.map((course) => course.segmentCourse.course.courseraCourse?.id).filter(Boolean);
  return courses.filter(
    // @ts-expect-error TS-reset TS2345
    (progress, index) => !courseraIds.includes(progress.segmentCourse.course.courseraCourse?.id, index + 1)
  );
};

/**
 * Extract the courses with the IN_PROGRESS state from a list of courses
 * @param courses the given list of courses
 * @returns the courses with the IN_PROGRESS state
 */
const getInProgressCourses = (courses: DpPersonalizedSegmentCourse[]): DpPersonalizedSegmentCourse[] => {
  return courses.filter((course) => course.learnerProgress.state === 'IN_PROGRESS');
};

/**
 * Extract the courses with the PLANNED state from a list of courses
 * @param courses the given list of courses
 * @returns the courses with the PLANNED state
 */
const getPlannedCourses = (courses: DpPersonalizedSegmentCourse[]): DpPersonalizedSegmentCourse[] => {
  return courses.filter((course) => course.learnerProgress.state === 'PLANNED');
};

/**
 * Based on the min passing grade, check if the learner passes the course
 * @param minPassingGrade the min passing grade
 * @param course the course
 * @returns true if the grade >= the min passing grade, false otherwise
 */
export const isCoursePassed = (minPassingGrade: number, course: DpPersonalizedSegmentCourse): boolean => {
  const learnerGrade = course.learnerProgress.grade;
  return typeof learnerGrade === 'number' && learnerGrade >= minPassingGrade;
};

/**
 * Calculate the total credit value for COMPLETED and IN_PROGRESS courses
 * @param curriculum the curriculum
 * @returns {Object} with the total credit values for completed and inprogress courses
 */
export const getTotalCreditValue = (curriculum: DpDegreeCurriculum | DpPbaCurriculum) => {
  const segments = curriculum.categories.map((category) => category.segments).flat();
  const courses = segments.map((segment) => segment.courses).flat();

  const completedCourses = getCompletedCourses(courses);
  const inProgressCourses = getInProgressCourses(courses);
  const inProgressCourseCreditValue = getSumOfCredits(dedupeByCourseraCourseId(inProgressCourses));

  const completedCourseCreditValue = getSumOfCredits(dedupeByCourseraCourseId(completedCourses));
  return { completedCourseCreditValue, inProgressCourseCreditValue };
};

/**
 * Takes in a curriculum, use the overall progression to determine if the learner has met all requirements to complete the degree, including:
 * 1. does the learner complete enough credits and requirements from the underlying categories (indicated by "creditsAndCategoryRequirementsMet")
 * 2. no curriculum-level warnings
 * @param curriculum the curriculum
 * @param overallProgression the overall progression, calculated by the progression calculator
 * @returns {boolean} true if the learner has met all requirements of the degree, false otherwise
 */
export const verifyDegreeCompletionStatus = (
  curriculum: DpDegreeCurriculum | DpPbaCurriculum,
  overallProgression: ProgressionCalculatorResult | null
): boolean => {
  const curriculumProgress = overallProgression?.curriculums?.[curriculum.id];
  const curriculumWarnings = curriculumProgress?.warnings ?? { gpa: false, grade: false };
  return (
    !!curriculumProgress?.creditsAndCategoryRequirementsMet &&
    Object.values(curriculumWarnings).every((hasWarning) => !hasWarning)
  );
};

/**
 * Based on the required min passing grade, calculate the total number of completed credits and in-progress credits for a segment
 * @param minPassingGrade the min passing grade
 * @param segment the segment
 * @returns the summary of completed and in-progress credits
 */
export const getCreditsSummaryForSingleSegment = (
  minPassingGrade: number,
  segment: DpSegment
): CreditsAndCoursesSummary => {
  const courses = segment.courses;
  const completedCourses = courses.filter(
    (course) =>
      course.learnerProgress.state === 'COMPLETED' &&
      typeof course.learnerProgress.grade === 'number' &&
      isCoursePassed(minPassingGrade, course)
  );
  const completedCredits = getSumOfCredits(completedCourses);
  const inProgressCourses = getInProgressCourses(courses);
  const inProgressCredits = getSumOfCredits(inProgressCourses);
  const plannedCourses = getPlannedCourses(courses);
  const plannedCredits = getSumOfCredits(plannedCourses);

  return {
    completed: completedCredits,
    inProgress: inProgressCredits,
    planned: plannedCredits,
    completedCourses,
    inProgressCourses,
    plannedCourses,
  };
};

/**
 * Given a list of courses, calculate the total number of completed credits and in-progress credits for a segment
 * @param courses an array of courses
 * @returns the summary of completed and in-progress credits
 */
export const getCreditSummaryForCourses = (courses: DpPersonalizedSegmentCourse[]): CreditsSummary => {
  const completeCourses = getCompletedCourses(courses);
  const completedCredits = getSumOfCredits(completeCourses);

  const inProgressCourses = getInProgressCourses(courses);
  const inProgressCredits = getSumOfCredits(inProgressCourses);

  const plannedCourses = getPlannedCourses(courses);
  const plannedCredits = getSumOfCredits(plannedCourses);

  return { completed: completedCredits, inProgress: inProgressCredits, planned: plannedCredits };
};

/**
 * Check if a category has grade warning
 * @param category the category
 * @param completedCourses the list of completed courses
 * @returns true if the category should display the grade warning, false otherwise
 */
const categoryHasGradeWarning = (category: DpCategory, completedCourses: DpPersonalizedSegmentCourse[]): boolean => {
  // the category has the grade warning if all of followings are true:
  // 1. the completion rule of the category is met (enforced by getWarningsForSingleCategory)
  // 2. the category has a category-level requirement for min passing grade
  // 3. there are courses completed with grades lower than the category-level min passing grade
  return typeof category.minPassingGradeForCourses === 'number'
    ? completedCourses.filter((course) => !isCoursePassed(category.minPassingGradeForCourses as number, course))
        .length > 0
    : false;
};

/**
 * Check if a category has GPA warning
 * @param category the category
 * @param completedCourses the list of completed courses
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns an Object indicating whether the GPA warning should be displayed and the category GPA (null if no GPA warning is to be displayed)
 */
const categoryHasGPAWarning = (
  category: DpCategory,
  completedCourses: DpPersonalizedSegmentCourse[],
  gradeScale: GradeScaleFragment
): { hasGPAWarning: boolean; categoryGPA: GPA } => {
  // the category has the GPA warning if all of the followings are true:
  // 1. the completion rule of the category is met (enforced by getWarningsForSingleCategory)
  // 2. the category has a category-level requirement for min GPA
  // 3. the GPA of the courses under the category is lower than the category-level min GPA
  if (typeof category.minGpa === 'number') {
    const categoryGPA = getGPAFromCourses(completedCourses, gradeScale);
    const hasGPAWarning = typeof categoryGPA === 'number' && categoryGPA < category.minGpa;
    return { hasGPAWarning, categoryGPA };
  }
  return { hasGPAWarning: false, categoryGPA: null };
};

/**
 * Calculate the GPA and grade warning for the category
 * @param category the category
 * @param categoryCompletionRuleMet whether the category's completion rule is met
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns the GPA and grades warnings, and the category GPA
 */
export const getWarningsForSingleCategory = (
  category: DpCategory,
  categoryCompletionRuleMet: boolean,
  gradeScale: GradeScaleFragment
): Warnings & { categoryGPA: GPA } => {
  // if the category's completion rule is not met, don't display warnings and GPA doesn't need to be calculated
  if (!categoryCompletionRuleMet) {
    return { gpa: false, grade: false, categoryGPA: null };
  }
  const courses = dedupeByCourseraCourseId(category.segments.map((segment) => segment.courses).flat());
  const completedCourses = getCompletedCourses(courses);
  const failedCourses = getFailedCourses(courses);

  const gradedCourses = [...completedCourses, ...failedCourses];

  const { hasGPAWarning, categoryGPA } = categoryHasGPAWarning(category, gradedCourses, gradeScale);
  const hasGradeWarning = categoryHasGradeWarning(category, gradedCourses);

  return { gpa: hasGPAWarning, grade: hasGradeWarning, categoryGPA };
};

/**
 * Check if a curriculum has grade warning
 * @param curriculum the curriculum
 * @returns true if the curriculum should display the grade warning, false otherwise
 */
export const curriculumHasGradeWarning = (curriculum: DpDegreeCurriculum | DpPbaCurriculum): boolean => {
  // the curriculum-level requirement of min passing grade
  // only applies to categories (and the courses under those categories)
  // that don't have category-level requirement of min passing grade
  const categoriesWithoutMPG = curriculum.categories.filter(
    (category) => typeof category.minPassingGradeForCourses !== 'number'
  );
  const coursesUnderCategoriesWithoutMPG = dedupeByCourseraCourseId(
    categoriesWithoutMPG
      .map((category) => category.segments)
      .flat()
      .map((segment) => segment.courses)
      .flat()
  );
  const completedCoursesUnderCategoriesWithoutMPG = getCompletedCourses(coursesUnderCategoriesWithoutMPG);
  const failedCoursesUnderCategoriesWithoutMPG = getFailedCourses(coursesUnderCategoriesWithoutMPG);

  const gradedCoursesUnderCategoriesWithoutMPG = [
    ...completedCoursesUnderCategoriesWithoutMPG,
    ...failedCoursesUnderCategoriesWithoutMPG,
  ];

  // the curriculum has the grade warning if all of the followings are true
  // 1. the learner already completed more credits than required by the curriculum (enforced by getWarningsForSingleCurriculum)
  // 2. all underlying categories' completion rules are met (enforced by getWarningsForSingleCurriculum)
  // 3. no underlying categories have category-level warnings (enforced by getWarningsForSingleCurriculum)
  // 4. among all courses from the categories without category-level requirement of min passing grade,
  //    there are courses completed with grade lower than the curriculum-level min passing grade
  return (
    gradedCoursesUnderCategoriesWithoutMPG.filter(
      (course) => !isCoursePassed(curriculum.minPassingGradeForCourses, course)
    ).length > 0
  );
};

/**
 * Check if a curriculum has GPA warning
 * @param curriculum the curriculum
 * @param gradeScale the GPA Grade scale used to map grades to GPA points for the degree
 * @returns an Object indicating whether the GPA warning should be displayed and the curriculum GPA (null if no GPA warning is to be displayed)
 */
export const curriculumHasGPAWarning = (
  curriculum: DpDegreeCurriculum | DpPbaCurriculum,
  gradeScale: GradeScaleFragment
): { hasGPAWarning: boolean; curriculumGPA: GPA } => {
  // similar as curriculum's min passing grade, the curriculum-level requirement of min GPA
  // only applies to categories (and the courses under those categories)
  // that don't have category-level requirement of min GPA
  const categoriesWithoutMinGPA = curriculum.categories.filter((category) => typeof category.minGpa !== 'number');
  const coursesUnderCategoriesWithoutGPA = dedupeByCourseraCourseId(
    categoriesWithoutMinGPA
      .map((category) => category.segments)
      .flat()
      .map((segment) => segment.courses)
      .flat()
  );
  const completedCoursesUnderCategoriesWithoutMinGPA = getCompletedCourses(coursesUnderCategoriesWithoutGPA);
  const failedCoursesUnderCategoriesWithoutMinGPA = getFailedCourses(coursesUnderCategoriesWithoutGPA);

  const gradedCoursesUnderCategoriesWithoutMinGPA = [
    ...completedCoursesUnderCategoriesWithoutMinGPA,
    ...failedCoursesUnderCategoriesWithoutMinGPA,
  ];

  // the curriculum has the GPA warning if all of the followings are true
  // 1. the learner already completed more credits than required by the curriculum (enforced by getWarningsForSingleCurriculum)
  // 2. all underlying categories' completion rules are met (enforced by getWarningsForSingleCurriculum)
  // 3. no underlying categories have category-level warnings (enforced by getWarningsForSingleCurriculum)
  // 4. for all courses from the categories without category-level requirement of min passing grade,
  //    the GPA of those courses is lower than the curriculum-level min GPA
  const gpaForCoursesUnderCategoriesWithoutGPA = getGPAFromCourses(
    gradedCoursesUnderCategoriesWithoutMinGPA,
    gradeScale
  );
  return {
    hasGPAWarning:
      typeof gpaForCoursesUnderCategoriesWithoutGPA === 'number' &&
      gpaForCoursesUnderCategoriesWithoutGPA < curriculum.minGpa,
    curriculumGPA: gpaForCoursesUnderCategoriesWithoutGPA,
  };
};

export const roundGrade = (grade: number) => Math.floor(grade * 100) / 100;

/** Convert a grade that ranges between 0-1 to a rounded 0-100 value for display as percentage */
export const convertUnitIntervalGradeToPercentageValue = (grade: number) => roundGrade(grade * 100);

/**
 * retrieve the externalCourseId from the segmentCourse's ID
 * @param course the segmentCourse
 * @returns the externalCourseId, or undefined if not
 */
export const getExternalCourseId = (id: string | undefined | null): string | undefined => {
  // TODO: https://coursera.atlassian.net/browse/CPM-3125 clean up when segment course uuid experiment is removed
  if (id) {
    // base~O30WHIkRQJ-vtPeTt_a7xw~SEG-12~26~CM3040-01 is converted to ['base', 'O30WHIkRQJ-vtPeTt_a7xw', 'SEG-12', '26', 'CM3040-01']
    const parts = id.split('~');
    if (parts.length === 5) {
      return `${parts[3]}~${parts[4]}`;
    }
  }

  return undefined;
};

/**
 * Determine if a duplicate course is present in a given curriculum
 * @param curriculum the curriculum
 * @returns true if a duplicate segmentCourse is found
 */
export const curriculumHasDuplicatedCourses = (curriculum: DpDegreeCurriculum | DpPbaCurriculum): boolean => {
  const allCourseCodes = curriculum.categories
    .flatMap((category) => category.segments)
    .flatMap((segment) => segment.courses)
    .map((DpPersonalizedSegmentCourse) => getExternalCourseId(DpPersonalizedSegmentCourse?.segmentCourse?.id))
    .filter(Boolean);

  const uniqueCourseCodes = [...new Set(allCourseCodes)];

  return allCourseCodes.length !== uniqueCourseCodes.length;
};

/**
 * Calculate the median credit value of a list of courses
 * @param courses the given list of courses
 * @returns the median of the credit values
 */
export const getMedianCreditValue = (courses: DpPersonalizedSegmentCourse[]): number | null => {
  if (!courses.length) return null;
  const creditValues = courses.map((course) => course.segmentCourse.creditValue);
  const sortedCreditValues = creditValues.sort((a, b) => a - b);
  const middle = Math.floor(creditValues.length / 2);

  const median =
    creditValues.length % 2
      ? sortedCreditValues[middle]
      : // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        (sortedCreditValues[middle - 1] + sortedCreditValues[middle]) / 2;

  // always return a whole number
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return Math.floor(median);
};
